import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Button } from 'ui-components/components/button';

import { sellerProvidersUpdated } from '../../../../store/seller/seller.actions';
import { useSellerLabelProviderBalance } from '../../providers/providers-list/contexts/seller-label-provider-data-context-provider';
import { useModal } from '../../../../hooks/use-modal';
import AddFundsModal from '../../providers/add-funds-modal';
import useSellerLabelProviderAddFunds from '../../providers/add-funds-modal/hooks/use-seller-label-provider-add-funds';
import { useSellerLabelProvider } from '../../../../hooks/use-seller-label-provider';
import useSaveSellerLabelProviderDefaultFundingAmount from '../../providers/add-funds-modal/hooks/use-save-seller-label-provider-default-funding-amount';
import useSaveSellerLabelProviderAutoFunding from '../../providers/add-funds-modal/hooks/use-save-seller-label-provider-auto-funding';
import FormattedCurrency from '../../../../components/formatted-currency';
import messages from '../messages';
import style from './carriers-additional.module.less';

/**
 * At the moment, this is only used for BYOA carriers which maintain a balance
 * @deprecated
 * use .../payment-and-subscription/components/add-funds-modal/add-funds-modal.js or .../payment-and-subscription/components/funding/confirmation-modal/index.js instead,
 * paired with \<AutoFundingModal /\>
 * @param {object} props
 * @param {number} props.sellerLabelProviderId
 * @returns {JSX.Element}
 */
export const BalanceInfo = ({ sellerLabelProviderId }) => {
  const { loadingBalancesMap, balance } = useSellerLabelProviderBalance(sellerLabelProviderId);
  const sellerLabelProvider = useSellerLabelProvider(sellerLabelProviderId) || {};
  const onAddFunds = useSellerLabelProviderAddFunds(sellerLabelProviderId);
  const onSaveDefaultFundingAmount = useSaveSellerLabelProviderDefaultFundingAmount(sellerLabelProviderId);
  const onSaveAutoFunding = useSaveSellerLabelProviderAutoFunding(sellerLabelProviderId);
  const { open, close, isOpen } = useModal();
  const dispatch = useDispatch();
  const onSuccess = useCallback(() => {
    dispatch(sellerProvidersUpdated());
    close();
  }, [close, dispatch]);

  if (loadingBalancesMap && !balance) {
    return <LoadingIndicator className={style['loading-balance']} />;
  }

  return (
    <>
      <FormattedMessage
        {...messages.balanceLabel}
        values={{
          balance: <FormattedCurrency currencyCode={balance?.code} value={balance?.value} />,
        }}
      />
      <Button onClick={open} theme='link'>
        <FormattedMessage {...messages.addFundsLink} />
      </Button>

      {isOpen && (
        <AddFundsModal
          allowAutoFunding
          allowDefaultPurchase
          autoFundConfig={{
            threshold: sellerLabelProvider?.autoFund?.threshold,
            amount: sellerLabelProvider?.autoFund?.purchaseAmount,
            cutoff: sellerLabelProvider?.autoFund?.maxPerDay,
          }}
          balance={balance}
          defaultPurchaseConfig={sellerLabelProvider?.defaultPurchaseAmount}
          onAddFunds={onAddFunds}
          onClose={close}
          onSaveAutoFunding={onSaveAutoFunding}
          onSaveDefaultFundingAmount={onSaveDefaultFundingAmount}
          onSuccess={onSuccess}
        />
      )}
    </>
  );
};

BalanceInfo.propTypes = {
  sellerLabelProviderId: PropTypes.string.isRequired,
};
